import { useCallback, useEffect, useState } from "react";
import { api } from "../services/api";
import { esEmailValido } from "../utils/email";

/* Datos y operaciones del staff de un evento. Lo usan el panel de Staff y el
 * paso de colaboradores del alta, que antes tenían cada uno su propia copia
 * de esta lógica.
 *
 * `ocultarUsuarioId` saca de la lista a un usuario (en el alta, quien crea el
 * evento no necesita verse a sí mismo). */

const normalizar = (p) => ({
  id: p.id,
  idusuario: p.idusuario,
  idrol: p.idrol,
  nombre: `${p.usuario?.nombre || ""} ${p.usuario?.apellido || ""}`.trim(),
  email: p.usuario?.usuario || "",
  observacion: p.observacion || "",
  tienePin: p.tiene_pin === true,
});


const useStaff = (eventoId, { ocultarUsuarioId = null, filasPorPaginaInicial = 10 } = {}) => {
  const [miembros, setMiembros] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [busquedaInput, setBusquedaInput] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(0);
  const [filasPorPagina, setFilasPorPagina] = useState(filasPorPaginaInicial);

  // la búsqueda real espera a que se deje de escribir
  useEffect(() => {
    const timeout = setTimeout(() => {
      setBusqueda(busquedaInput.trim());
      setPagina(0);
    }, 400);
    return () => clearTimeout(timeout);
  }, [busquedaInput]);

  const recargar = useCallback(async ({ silencioso = false } = {}) => {
    if (!eventoId) return;
    if (!silencioso) setCargando(true);
    try {
      const response = await api.participaciones.getParticipacionesByEvento(
        eventoId, busqueda, filasPorPagina, pagina + 1
      );
      if (response.status !== "success") {
        setError(response.message || "No se pudo cargar el staff.");
        return;
      }

      const todos = (response.data?.participaciones ?? []).map(normalizar);
      const visibles = ocultarUsuarioId
        ? todos.filter((m) => m.idusuario !== ocultarUsuarioId)
        : todos;
      const totalVisible = Math.max(
        (response.data?.totalDataSize || 0) - (todos.length - visibles.length),
        0
      );

      setMiembros(visibles);
      setTotal(totalVisible);
      setError(null);
      if (pagina > 0 && pagina * filasPorPagina >= totalVisible) setPagina(0);
    } catch (err) {
      console.error("Error al cargar el staff:", err);
      setError("No se pudo conectar con el servidor.");
    } finally {
      if (!silencioso) setCargando(false);
    }
  }, [eventoId, busqueda, filasPorPagina, pagina, ocultarUsuarioId]);

  useEffect(() => {
    recargar();
  }, [recargar]);

  /* Alta: el usuario tiene que estar registrado. Devuelve {ok, error}. El
   * aviso por mail lo manda el backend al crear la participación. */
  const agregar = async ({ email, idrol, observacion }) => {
    const mail = (email || "").trim();
    if (!mail) return { ok: false, error: "El correo electrónico es obligatorio." };
    if (!esEmailValido(mail)) return { ok: false, error: "Introducí un correo electrónico válido." };
    if (!idrol) return { ok: false, error: "Elegí un rol (y el tipo de colaborador, si corresponde)." };

    try {
      const existe = await api.usuarios.getUserExist(mail);
      if (existe.status === "error") {
        return {
          ok: false,
          error: "El usuario con este correo no está registrado. Pedile que se registre primero.",
        };
      }

      const response = await api.participaciones.createParticipacion({
        idevento: Number(eventoId),
        idusuario: existe.data.usuario.id,
        idrol,
        email: mail,
        observacion: observacion || "",
      });
      if (response.status !== "success") {
        return { ok: false, error: response.message || "No se pudo agregar el miembro." };
      }

      recargar({ silencioso: true });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || "No se pudo conectar con el servidor." };
    }
  };

  /* Cambio de rol y/o observación. Siempre se manda la observación: el
   * backend la guarda tal cual llega, y omitirla la borraba. */
  const actualizar = async (miembro, { idrol, observacion }) => {
    if (miembro.idrol === idrol && miembro.observacion === observacion) return { ok: true };
    try {
      const response = await api.participaciones.updateSubrol({
        idparticipacion: miembro.id,
        newRol: idrol,
        newSubrol: 0,
        observacion,
      });
      if (response.status !== "success") {
        return { ok: false, error: response.message || "No se pudo actualizar el miembro." };
      }
      recargar({ silencioso: true });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || "No se pudo conectar con el servidor." };
    }
  };

  const asignarPin = async (miembro, pin) => {
    try {
      const response = await api.participaciones.setPinVendedor({ idparticipacion: miembro.id, pin });
      if (response.status !== "success") {
        return { ok: false, error: response.message || "No se pudo asignar el PIN." };
      }
      recargar({ silencioso: true });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || "No se pudo conectar con el servidor." };
    }
  };

  return {
    miembros,
    total,
    cargando,
    error,
    busquedaInput,
    setBusquedaInput,
    busqueda,
    pagina,
    setPagina,
    filasPorPagina,
    setFilasPorPagina: (n) => {
      setFilasPorPagina(n);
      setPagina(0);
    },
    recargar,
    agregar,
    actualizar,
    asignarPin,
  };
};

export default useStaff;
